import { Star } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import type { PlanLevel, PlanRow } from './planRows';

/** How many earlier concepts a row names before "+N". */
const BUILDS_ON_SHOWN = 2;

/** The concept's level as a mark: filled, half, ring. */
export function LevelMark({ level }: { level: PlanLevel }) {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block h-2.5 w-2.5 shrink-0 rounded-full border-2 border-lesson',
        level === 'foundational' && 'bg-lesson',
        level === 'core' && 'bg-lesson/35',
        level === 'advanced' && 'bg-transparent'
      )}
    />
  );
}

function SectionRow({ row }: { row: Extract<PlanRow, { kind: 'section' }> }) {
  return (
    <div className='flex gap-3 pb-1 pt-4 first:pt-1'>
      <span className='mt-px flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lesson/15 font-mono text-xs font-medium text-lesson'>
        {row.number}
      </span>
      <div className='min-w-0'>
        <InlineMarkdown className='block text-sm font-semibold text-foreground'>
          {row.title}
        </InlineMarkdown>
        {row.goal && (
          <InlineMarkdown className='block text-xs leading-relaxed text-muted-foreground'>
            {row.goal}
          </InlineMarkdown>
        )}
      </div>
    </div>
  );
}

function buildsOnText(names: string[]): string {
  const shown = names.slice(0, BUILDS_ON_SHOWN).join(', ');
  const more = names.length - BUILDS_ON_SHOWN;
  return more > 0 ? `${shown} +${more}` : shown;
}

function ConceptRow({ row }: { row: Extract<PlanRow, { kind: 'concept' }> }) {
  return (
    <div className='flex items-start gap-2.5 py-1.5 pl-9'>
      <span className='mt-1.5'>
        <LevelMark level={row.level} />
      </span>
      <div className='min-w-0 flex-1'>
        <div className='flex items-baseline gap-2'>
          <InlineMarkdown className='min-w-0 flex-1 truncate text-sm text-foreground'>
            {row.name}
          </InlineMarkdown>
          {row.keyIdea && (
            <Star
              aria-label='Key idea'
              className='h-3.5 w-3.5 shrink-0 self-center fill-lesson text-lesson'
            />
          )}
          <span className='shrink-0 font-mono text-xs text-muted-foreground'>
            {row.points} {row.points === 1 ? 'point' : 'points'}
          </span>
        </div>
        {row.buildsOn.length > 0 && (
          <p className='truncate text-xs text-muted-foreground'>
            Builds on {buildsOnText(row.buildsOn)}
          </p>
        )}
      </div>
    </div>
  );
}

export function PlanRowView({ row }: { row: PlanRow }) {
  return row.kind === 'section' ? (
    <SectionRow row={row} />
  ) : (
    <ConceptRow row={row} />
  );
}
